import {
  MajorButtonComponent,
  SpinnerComponent,
  TextFieldComponent,
} from "@zenith/components";
import { useNotificationContext } from "@zenith/context";
import { EMAIL_REGEX } from "@zenith/data/constants";
import {
  OperationsApiService,
  PaymentLinkPublicApiService,
} from "@zenith/services/transactions";
import { memo, useState } from "react";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ShareServiceFailureComponent from "../share-service-failure-component";

/* eslint-disable-next-line */
export interface ShareReceiptComponentProps {
  operationId: string;
  notificationClass: string;
  isPaymentLink?: boolean;
  /** Small "Email this Receipt to customer" field with an icon send button. */
  compact?: boolean;
  notify: (
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ) => void;
}

const ShareReceiptComponent = memo(function ShareReceiptComponent(
  props: ShareReceiptComponentProps
) {
  const { notify } = props;
  const [hasError, setHasError] = useState(false);
  const [retryFn, setRetryFn] = useState<(() => Promise<void>) | undefined>();

  const [formData, setFormData] = useState<{ email: string }>({
    email: "",
  });

  const [emailError, setEmailError] = useState<{
    message: string;
    hasError: boolean;
  }>({ message: "", hasError: false });
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function onEmailChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const email = event.currentTarget.value.replace(/\s+/g, "");
    setFormData({ email });
    // Errors are only raised on blur; drop a stale one as soon as the typed
    // value becomes valid so it does not keep the send button disabled.
    if (emailError.hasError && EMAIL_REGEX.test(email)) {
      setEmailError({ hasError: false, message: "" });
    }
  }

  function onEmailBlurChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const email = event.currentTarget.value;
    setEmailError({
      hasError: false,
      message: "",
    });

    if (email.length < 1) {
      setEmailError({
        hasError: true,
        message: "Email address is required",
      });
    } else if (!EMAIL_REGEX.test(email)) {
      setEmailError({
        hasError: true,
        message: "Enter a valid email address",
      });
    }
  }

  function handleEmailSend() {
    setIsLoading(true);

    const requestData = { email: formData.email };

    const apiCall = props.isPaymentLink
      ? () =>
          PaymentLinkPublicApiService.sharePaymentLinkReceipt(
            props.operationId,
            requestData
          )
      : () => OperationsApiService.shareReceipt(props.operationId, requestData);

    apiCall()
      .then(() => {
        props.notify(
          "Receipt Sent Successfully",
          `The receipt has been sent to ${formData.email}.`,
          "success"
        );
      })
      .catch((error: any) => {
        const apiMessage =
          error?.result?.body?.message ||
          error?.body?.message ||
          error?.message ||
          null;

        const isBusinessError =
          error?.status === 400 || error?.status === 404 || apiMessage;

        if (isBusinessError) {
          notify(
            "Unable to Send Receipt",
            apiMessage || "Unable to send receipt",
            "error"
          );
        }
      })
      .finally(() => setIsLoading(false));
  }

  const isSendDisabled = !EMAIL_REGEX.test(formData.email) || isLoading;

  if (props.compact) {
    return (
      <>
        <p aria-hidden="true" className="mb-1 text-xs text-neutral-500">
          Email this Receipt to customer
        </p>
        <div className="flex w-full flex-row items-start gap-2">
          <div className="flex-1">
            <TextFieldComponent
              id="emailField"
              label="Email this Receipt to customer"
              labelClassName="sr-only"
              labelId="emailFieldLabel"
              type="text"
              placeholder="Enter Email"
              // The global .text-field rule forces 48px; match the 40px send button.
              className="!h-10 border-neutral-200 bg-white shadow-none"
              size="md"
              value={formData.email}
              hasError={emailError.hasError}
              onChange={onEmailChange}
              onBlur={onEmailBlurChange}
            />
            {emailError.hasError && (
              <div className="mt-1 text-xs text-red-400">
                <span className="break-words">{emailError.message}</span>
              </div>
            )}
          </div>
          <button
            id="sendEmailButton"
            type="button"
            aria-label="Send receipt by email"
            onClick={handleEmailSend}
            disabled={isSendDisabled}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-300 text-white hover:bg-primary-400 disabled:bg-zinc-100 disabled:text-gray-300"
          >
            {isLoading ? (
              <SpinnerComponent size={20} type="primary" />
            ) : (
              <SendOutlinedIcon fontSize="small" />
            )}
          </button>
        </div>

        <ShareServiceFailureComponent
          hasError={hasError}
          onClose={() => setHasError(false)}
          retryFn={retryFn}
        />
      </>
    );
  }

  return (
    <>
      <div className="mt-6 flex flex-row items-end gap-3 w-full">
        <div className="flex-1">
          <TextFieldComponent
            id="emailField"
            label="Please provide a valid email address to send the receipt"
            labelId="emailFieldLabel"
            type="text"
            placeholder="Enter Email Address"
            className="border-neutral-200 bg-white shadow-none"
            size="md"
            value={formData.email}
            hasError={emailError.hasError}
            onChange={onEmailChange}
            onBlur={onEmailBlurChange}
          />
          {emailError.hasError && (
            <div className="mt-1 text-xs text-red-400">
              <span className="break-words">{emailError.message}</span>
            </div>
          )}
        </div>
        <div>
          <MajorButtonComponent
            id="sendEmailButton"
            buttonType="secondary"
            type="button"
            size="large"
            widerButton
            className="h-12 whitespace-nowrap px-6 text-xs font-semibold rounded-xl border border-neutral-300 shadow-none hover:bg-neutral-50 flex items-center justify-center"
            onClick={handleEmailSend}
            disabled={
              emailError.hasError || formData.email.length < 1 || isLoading
            }
          >
            {isLoading ? (
              <SpinnerComponent size={20} type="primary" />
            ) : (
              "Send Email"
            )}
          </MajorButtonComponent>
        </div>
      </div>

      <ShareServiceFailureComponent
        hasError={hasError}
        onClose={() => setHasError(false)}
        retryFn={retryFn}
      />
    </>
  );
});

export default ShareReceiptComponent;
